import { Select as WebSDKSelect } from '@zambezi/sdk/dropdown-list';
import classNames from 'classnames';
import React, { ForwardedRef, useId } from 'react';
import FormControl from '../FormControl';
import './Select.scss';

interface Option<T = string> {
  label: string;
  value: T;
}

export interface SelectProps<T> {
  label: React.ReactNode | React.ReactNode[];
  name: string;
  suggestions?: Option<T>[] | T[];
  placeholder?: string;
  disabled?: boolean;
  disabledItemsIndices?: number[];
  required?: boolean;
  error?: string | boolean;
  value?: T;
  tooltip?: string | React.ReactNode | React.ReactNode[];
  tooltipAriaLabel?: string;
  helpText?: React.ReactNode | React.ReactNode[];
  hideLabel?: boolean;
  pii?: boolean;
  inputProps?: React.HTMLAttributes<HTMLElement>;
  fullWidth?: boolean;
  suggestionRenderer?: (label: string) => React.ReactNode;
  selectedSuggestionRenderer?: (label: string) => string;
  onChange?: (value: T) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

function SelectInner<T>(
  {
    label,
    placeholder,
    disabled = false,
    disabledItemsIndices = [],
    required = false,
    pii = false,
    value,
    error,
    suggestions = [],
    tooltip,
    tooltipAriaLabel,
    helpText,
    hideLabel = false,
    inputProps = {},
    fullWidth,
    suggestionRenderer,
    onChange,
    onBlur,
    onFocus
  }: SelectProps<T>,
  ref: ForwardedRef<HTMLSelectElement>
) {
  const id = useId();

  const getOptionLabel = (suggestion: Option | string | number): string => {
    switch (typeof suggestion) {
      case 'string':
        return suggestion;
      case 'number':
        return `${suggestion}`;
      default:
        return suggestion.label;
    }
  };

  const getOptionValue = (suggestion: Option<T>): T => {
    switch (typeof suggestion) {
      case 'string':
      case 'number':
        return suggestion;
      default:
        return suggestion.value;
    }
  };

  const suggestionLabelRenderer = (label: string) =>
    suggestionRenderer ? suggestionRenderer(label) : label;

  const renderSuggestion = (suggestion: Option | string | number) => {
    const suggestionLabel = getOptionLabel(suggestion);
    const suggestionTestId = suggestionLabel;
    return (
      <span data-cy={suggestionTestId} data-testid={suggestionTestId}>
        {suggestionLabelRenderer(suggestionLabel)}
      </span>
    );
  };

  const resolveLabelFromValue = (valueToResolve: T) => {
    // If we are dealing with string suggestions then just return the value because
    // for string suggestions the label and the value are the same
    if (suggestions && typeof suggestions[0] === 'string') {
      return valueToResolve as string;
    }

    // If we are dealing with number suggestions then convert to string and
    // just return the value because for string suggestions the label and the
    // value are the same
    if (suggestions && typeof suggestions[0] === 'number') {
      return `${valueToResolve}`;
    }

    // If we are dealing with object suggestions then the value may be a string or TOptionValue
    // In this case we have to search for the suggestion that has a value property that equals
    // the value and return that suggestion's label.
    if (suggestions && typeof suggestions[0] === 'object') {
      const match = (suggestions as Option[]).find(
        (suggestion) => suggestion.value === valueToResolve
      );

      if (match) {
        return match.label;
      }
    }

    return undefined;
  };

  const resolvedValue =
    value === undefined ? '' : !!value || value === 0 ? resolveLabelFromValue(value) : '';

  return (
    <FormControl
      id={id}
      label={label}
      tooltip={tooltip}
      tooltipAriaLabel={tooltipAriaLabel}
      error={error}
      helpText={helpText}
      required={required}
      hideLabel={hideLabel}
      pii={pii}
    >
      <WebSDKSelect
        id={id}
        placeholder={placeholder}
        suggestions={suggestions}
        getSuggestionValue={getOptionLabel}
        renderSuggestion={renderSuggestion}
        isDisabled={disabled}
        disabledItemsIndices={disabledItemsIndices}
        isError={!!error}
        value={resolvedValue}
        onBlur={onBlur}
        onFocus={onFocus}
        onSuggestionSelected={(_, { suggestion }) => {
          onChange && onChange(getOptionValue(suggestion));
        }}
        renderInput={({
          /* eslint-disable @typescript-eslint/no-unused-vars */
          isFocused,
          isError,
          isDisabled,
          isOpen,
          focusedSuggestionValue,
          getSuggestionValue,
          value,
          placeholder,
          suggestions,
          /* eslint-enable @typescript-eslint/no-unused-vars */
          ...buttonProps
        }) => (
          // @ts-expect-error types not match between origin input and new button
          <button {...buttonProps} type="button">
            {value ? suggestionLabelRenderer(value) : placeholder}
          </button>
        )}
        inputProps={{
          'aria-describedby': error ? `error-${id}` : undefined,
          'aria-required': required,
          ...inputProps
        }}
        className={classNames('mss-select-input', { 'mss-select-input--full-width': fullWidth })}
        // have to ignore this because WebSDK don't export LookupRef
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
      />
    </FormControl>
  );
}

export default React.forwardRef(SelectInner) as <T>(
  props: SelectProps<T> & { ref?: ForwardedRef<HTMLSelectElement> }
) => ReturnType<typeof SelectInner>;
